// Bileli · shared/api — the cookie-auth HTTP client for the Bileli REST API (Model infra).
// Every Bileli feature slice fetches through this. Web auth is COOKIE-based (backend handoff
// docs/backend/telegram-auth-web-integration.md): each call sends `credentials:'include'` so the
// httpOnly `bileli_access` cookie rides along, and a 401 triggers a single-flight `/auth/refresh`
// + one retry. Tokens never touch JS. Self-contained (no cross-package import) to keep Vite simple.
//
// Moved from `src/util/bileli/bileliApi.ts` in Phase Z (clean-arch) — that path is now a re-export shim.

// Configurable base: local dev points at the docker backend (set BILELI_API_BASE in the gitignored
// .env, e.g. http://localhost:8080); default/prod is api.bileli.kg.
export const BILELI_API_BASE = import.meta.env.TG_BILELI_API_BASE || 'https://api.bileli.kg';

let refreshInFlight: Promise<boolean> | undefined;

// Single-flight refresh: concurrent 401s must share ONE POST /auth/refresh rather than stampede it
// (the backend rotates the refresh cookie, so parallel refreshes would race + revoke).
async function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${BILELI_API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        return res.ok;
      } catch {
        return false;
      } finally {
        refreshInFlight = undefined;
      }
    })();
  }
  return refreshInFlight;
}

export interface BileliFetchOptions extends RequestInit {
  // Skip the 401→refresh→retry — the /auth/* calls themselves must not recurse into a refresh
  // (verify has no session yet; refresh refreshing itself would loop).
  skipRefresh?: boolean;
}

export async function bileliApiFetch(path: string, options: BileliFetchOptions = {}): Promise<Response> {
  const { skipRefresh, headers, ...rest } = options;
  const url = `${BILELI_API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  const init: RequestInit = {
    ...rest,
    credentials: 'include',
    headers: { Accept: 'application/json', ...headers },
  };

  let res = await fetch(url, init);
  if (res.status === 401 && !skipRefresh) {
    const ok = await refreshSession();
    if (ok) res = await fetch(url, init); // retry once
  }
  return res;
}

/**
 * A failed Bileli API call. `detail` is the backend's RFC-7807 human message (e.g. "Set your name in
 * your profile before publishing a course") — viewmodels show it to the user instead of a generic string.
 * `problemType` is the RFC-7807 `type` URI — the payments 409s are DISCRIMINATED BY IT ("payment-claimed"
 * vs "payment-approval-pending" vs plain conflict; openapi.yaml says branch on `type`, never on prose).
 */
export class BileliApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly detail?: string,
    public readonly problemType?: string,
  ) {
    super(message);
    this.name = 'BileliApiError';
  }
}

// Read the error body once and lift the RFC-7807 `detail` + `type` out (backend sends it as
// application/json, not problem+json — key off the body, not the content-type). `type` is kept only when
// it names a real problem ("about:blank" is the RFC default = no discriminator → undefined). Non-JSON
// bodies fall back to the generic string.
async function raiseApiError(res: Response, path: string): Promise<never> {
  let detail: string | undefined;
  let problemType: string | undefined;
  try {
    const body = await res.text();
    const parsed = body ? JSON.parse(body) : undefined;
    if (parsed && typeof parsed.detail === 'string') detail = parsed.detail;
    if (parsed && typeof parsed.type === 'string' && parsed.type !== 'about:blank') problemType = parsed.type;
  } catch { /* not JSON — keep the generic message */ }
  throw new BileliApiError(detail ?? `Bileli API ${res.status} at ${path}`, res.status, detail, problemType);
}

export async function bileliApiJson<T>(path: string, options?: BileliFetchOptions): Promise<T> {
  const res = await bileliApiFetch(path, options);
  if (!res.ok) await raiseApiError(res, path);
  // Some 2xx responses are BODYLESS (e.g. 201 "Linked", 204s) — res.json() on those throws the cryptic
  // "Unexpected end of JSON input". Tolerate empty; endpoints that must return data still fail loudly
  // downstream at parseApi/zod with the endpoint name in the error.
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * For VOID endpoints (bodyless 201/204: links, reorders, deletes, complete-toggles). Unlike raw
 * bileliApiFetch it THROWS on !ok — the viewmodels' optimistic-revert catches depend on that; a plain
 * fetch call swallows failures and the UI silently diverges from the server.
 */
export async function bileliApiVoid(path: string, options?: BileliFetchOptions): Promise<void> {
  const res = await bileliApiFetch(path, options);
  if (!res.ok) await raiseApiError(res, path);
}
